import React, { useState } from 'react';
import {
  Wallet,
  CheckCircle,
  FlaskConical,
  Users,
  Compass,
  Settings,
  ChevronRight,
  Sparkles,
  Building,
  Building2,
  GraduationCap,
  BarChart3,
  GitBranch,
  Zap,
  BookOpen,
} from 'lucide-react';
import { USER_GOALS, getEstimatedTime, formatTime } from '../config/onboardingMedia';
import { hasRole } from '../config/navigation';

// Icon mapping
const ICON_MAP = {
  Wallet,
  CheckCircle,
  FlaskConical,
  Users,
  Compass,
  Settings,
  Building,
  Building2,
  GraduationCap,
  BarChart3,
  GitBranch,
};

// Tour depth options
const TOUR_DEPTHS = {
  lite: {
    id: 'lite',
    title: 'Quick Overview',
    description: 'A fast tour of key features',
    icon: Zap,
    timeMultiplier: 0.5, // Lite mode shows ~50% of steps
  },
  detailed: {
    id: 'detailed',
    title: 'Step-by-Step Guide',
    description: 'In-depth walkthrough with how-to details',
    icon: BookOpen,
    timeMultiplier: 1.0, // Full steps
  },
};

/**
 * GoalSelector - Branching path selector for personalized onboarding
 *
 * Asks users what they want to accomplish and shows only relevant steps.
 * Displayed at the start of onboarding to customize the experience.
 * Now includes tour depth selection (lite vs detailed).
 */
const GoalSelector = ({ userRoles = [], onSelectGoal, onSelect, onSkip }) => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [hoveredGoal, setHoveredGoal] = useState(null);
  const [tourDepth, setTourDepth] = useState('lite'); // Default to quick overview
  const [step, setStep] = useState('depth'); // 'depth' or 'goal'

  // Filter goals based on user roles
  const availableGoals = Object.values(USER_GOALS).filter(goal => {
    // If goal has no role requirements, it's available to everyone
    if (!goal.roles || goal.roles.length === 0) return true;
    // Check if user has any of the required roles
    return hasRole(userRoles, goal.roles);
  });

  const handleConfirm = () => {
    if (!selectedGoal) return;
    const handler = onSelectGoal || onSelect;
    // Pass both goal and depth to the handler
    handler?.(selectedGoal, tourDepth);
  };

  const handleDepthNext = () => {
    setStep('goal');
  };

  const handleBackToDepth = () => {
    setStep('depth');
  };

  // Calculate estimated time based on depth
  const getAdjustedTime = (goal) => {
    if (!goal.steps) return '~3 min';
    const baseTime = getEstimatedTime(goal.steps);
    const multiplier = TOUR_DEPTHS[tourDepth]?.timeMultiplier || 1;
    return formatTime(Math.round(baseTime * multiplier));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-6 py-8 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.4),transparent_40%)]" />

          <div className="relative">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-xl bg-white/20">
                <Sparkles className="w-6 h-6" />
              </div>
              <h2 className="text-2xl font-bold">
                {step === 'depth' ? 'How would you like to learn?' : 'What brings you here today?'}
              </h2>
            </div>
            <p className="text-white/80 text-sm max-w-lg">
              {step === 'depth'
                ? 'Choose between a quick overview or an in-depth walkthrough.'
                : "Tell us what you'd like to accomplish, and we'll customize your tour."}
            </p>
          </div>

          {/* Step indicator */}
          <div className="absolute top-4 right-4 flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${step === 'depth' ? 'bg-white' : 'bg-white/40'}`} />
            <div className={`w-2 h-2 rounded-full ${step === 'goal' ? 'bg-white' : 'bg-white/40'}`} />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'depth' ? (
            /* Tour Depth Selection */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.values(TOUR_DEPTHS).map(depth => {
                const Icon = depth.icon;
                const isSelected = tourDepth === depth.id;

                return (
                  <button
                    key={depth.id}
                    onClick={() => setTourDepth(depth.id)}
                    className={`relative p-6 rounded-xl border-2 text-left transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-4 right-4">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col items-center text-center gap-3">
                      <div
                        className={`p-4 rounded-2xl transition-colors ${
                          isSelected
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}
                      >
                        <Icon className="w-8 h-8" />
                      </div>

                      <div>
                        <h3
                          className={`font-semibold text-lg ${
                            isSelected
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-800 dark:text-slate-100'
                          }`}
                        >
                          {depth.title}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                          {depth.description}
                        </p>
                      </div>

                      {depth.id === 'lite' && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          Recommended for first-time users
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            /* Goal Selection */
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
              {availableGoals.map(goal => {
                const Icon = ICON_MAP[goal.icon] || Compass;
                const isSelected = selectedGoal === goal.id;
                const isHovered = hoveredGoal === goal.id;

                return (
                  <button
                    key={goal.id}
                    onClick={() => setSelectedGoal(goal.id)}
                    onMouseEnter={() => setHoveredGoal(goal.id)}
                    onMouseLeave={() => setHoveredGoal(null)}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : isHovered
                        ? 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                    }`}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-lg transition-colors ${
                          isSelected
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                            : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>

                      <div className="flex-1 min-w-0 pr-6">
                        <h3
                          className={`font-semibold ${
                            isSelected
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-800 dark:text-slate-100'
                          }`}
                        >
                          {goal.title}
                        </h3>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                          {goal.description}
                        </p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-2">
                          {getAdjustedTime(goal)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
          {step === 'depth' ? (
            <>
              <button
                onClick={onSkip}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Skip for now
              </button>

              <button
                onClick={handleDepthNext}
                className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleBackToDepth}
                className="text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
              >
                Back
              </button>

              <button
                onClick={handleConfirm}
                disabled={!selectedGoal}
                className={`inline-flex items-center gap-2 px-6 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedGoal
                    ? 'text-white bg-blue-600 hover:bg-blue-700'
                    : 'text-slate-400 bg-slate-200 dark:bg-slate-700 cursor-not-allowed'
                }`}
              >
                Start {tourDepth === 'lite' ? 'Quick' : 'Full'} Tour
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoalSelector;
